import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { categories } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];

  const pageTitle = category
    ? `${category.name} - 91.con 导航`
    : "分类页面 - 91.con 导航";
  const pageDescription = category
    ? category.description
    : "91.con 内容分类导航";
  const canonicalUrl = `https://91con-nav.com/category/${slug || ""}`;

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">分类未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的分类页面不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`91.con,${category.name},91.con${category.name},91.con导航`}
        canonicalUrl={canonicalUrl}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: pageTitle,
          description: pageDescription,
          url: canonicalUrl,
        }}
        scriptId="schema-webpage"
      />

      <Navbar />

      <main className="min-h-screen pt-20 bg-background-50">
        {/* Category Header */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: category.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4 mt-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-500 text-white">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="mt-2 text-foreground-600 text-sm max-w-2xl">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200 bg-background-50 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              <Link
                to="/"
                className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors"
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    cat.slug === slug
                      ? "bg-primary-500 text-white"
                      : "text-foreground-600 hover:text-foreground-950 hover:bg-background-100"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {articles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={article.title}
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-400">{article.date}</span>
                        <span className="text-xs text-foreground-300">·</span>
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                      <h2 className="font-semibold text-foreground-950 text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="mt-4 flex items-center flex-wrap gap-1.5">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-background-100 text-foreground-500 rounded-md text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}